// texe-web/src/lib/retryUtils.ts
// Mobile'dan adapt edildi - Web için retry utilities

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: any) => boolean;
}

/**
 * Exponential backoff ile retry mechanism
 * Mobile'daki aynı implementation
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    retryCondition = (error: any) => {
      // Default: Network errors ve 5xx hatalarında retry yap
      return error?.message?.includes('Network') || 
             error?.message?.includes('fetch') ||
             (error?.status >= 500 && error?.status < 600) ||
             error?.status === 429; // Rate limit
    }
  } = options;

  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Son attempt'ta direkt hata fırlat
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Retry condition kontrolü
      if (!retryCondition(error)) {
        throw error;
      }
      
      // Exponential backoff delay
      const delay = Math.min(
        baseDelay * Math.pow(backoffMultiplier, attempt),
        maxDelay
      );
      
      console.log(
        `[RETRY] Attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${delay}ms...`,
        { error: (error as Error)?.message || String(error) }
      );
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Network error detection helper
 */
export function isRetryableError(error: any): boolean {
  // Auth errors kesinlikle retry edilmemeli
  if (error?.status === 401 || error?.status === 403) {
    return false;
  }
  
  // Network connection errors
  if (error?.message?.includes('fetch') || 
      error?.message?.includes('Network') ||
      error?.name === 'TypeError') {
    return true;
  }
  
  // Server errors (5xx)
  if (error?.status >= 500 && error?.status < 600) {
    return true;
  }
  
  // Rate limiting
  if (error?.status === 429) {
    return true;
  }
  
  // Timeout errors
  if (error?.code === 'TIMEOUT' || error?.message?.includes('timeout')) {
    return true;
  }
  
  return false;
}

/**
 * Pre-configured retry options for common scenarios
 */
export const RetryPresets = {
  // Critical operations (login, register, etc.)
  CRITICAL: {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 8000,
    retryCondition: isRetryableError
  } as RetryOptions,
  
  // Standard API calls
  STANDARD: {
    maxRetries: 2,
    baseDelay: 500,
    maxDelay: 5000,
    retryCondition: isRetryableError
  } as RetryOptions,
  
  // Background/non-critical operations
  BACKGROUND: {
    maxRetries: 1,
    baseDelay: 2000,
    maxDelay: 5000,
    retryCondition: (error: any) => {
      // Sadece network errors için retry, server errors için değil
      return error?.message?.includes('fetch') || 
             error?.message?.includes('Network') ||
             error?.name === 'TypeError';
    }
  } as RetryOptions,
  
  // Real-time operations (minimal retry)
  REALTIME: {
    maxRetries: 1,
    baseDelay: 1000,
    maxDelay: 2000,
    retryCondition: (error: any) => {
      // Sadece connection errors
      return error?.name === 'TypeError' || 
             error?.message?.includes('fetch');
    }
  } as RetryOptions
};